import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { GrossReceiptsStatusViewStore } from "../../store";
import { GrossReceiptsStatusView } from "../view/GrossReceiptsStatusView";
import { GrossReceiptsStatusUpdateStore } from "../../store";
import { GrossReceiptsStatusUpdate } from "../update/GrossReceiptsStatusUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type GrossReceiptsStatusModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const GrossReceiptsStatusModalContent: React.FC<GrossReceiptsStatusModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new GrossReceiptsStatusUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new GrossReceiptsStatusViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <GrossReceiptsStatusView
            grossReceiptsStatusViewStore={
              storeInfo.store as GrossReceiptsStatusViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <GrossReceiptsStatusUpdate
            grossReceiptsStatusUpdateStore={
              storeInfo.store as GrossReceiptsStatusUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
